import type { Column } from "@tanstack/react-table";
import type { CSSProperties } from "react";

interface GetCommonPinningStylesParams<TData, TValue> {
  column: Column<TData, TValue>;
  isRow?: boolean;
  disablePinning?: boolean;
}

/**
 * Computes common CSS styles for pinned table columns or rows.
 * @template TData - Table row data type
 * @template TValue - Column value type
 * @param {GetCommonPinningStylesParams<TData, TValue>} params - Parameters for computing pinning styles
 * @returns {CSSProperties} CSS styles for pinned elements
 */
/**
 * Computes common CSS styles for pinned table columns or rows.
 * @template TData - Table row data type
 * @template TValue - Column value type
 * @param {GetCommonPinningStylesParams<TData, TValue>} params - Parameters for computing pinning styles
 * @returns {CSSProperties} CSS styles for pinned elements
 */
export const getCommonPinningStyles = <TData, TValue>(
  params: GetCommonPinningStylesParams<TData, TValue>,
): CSSProperties => {
  const { column, isRow, disablePinning } = params;
  const isPinned = column.getIsPinned();

  if (disablePinning || !isPinned) {
    return {};
  }

  const styles: CSSProperties = {
    position: "sticky",
    zIndex: 9,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    boxShadow: column.columnDef.meta?.showRightBorder
      ? "-1px 0 1px -1px gray inset"
      : undefined,
  };

  // Background color
  if (!isRow) {
    styles.backgroundColor = "var(--bg-table-header)";
  }

  return styles;
};
